// api/providers/seriesContinueWatching.js
import { playListPassword, playListUsername, playListUrl } from "../../../state";
import { convertItemsToTilesSeries, chunkArraySeries } from "../formatters/ItemFormatter";
import { fetchData } from "../workers/fetchWorkerManager";
import { dnsResolver } from "./dnsResolver";

const continueWatchingCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// ✅ ADDED: Helper to get current playlist identifier
const getCurrentPlaylistId = () => {
  return `${playListUrl()}_${playListUsername()}`;
};

// ✅ MODIFIED: Filter by current playlist
const getContinueWatchingSeries = () => {
  try {
    const data = localStorage.getItem("series_continue_watching");
    if (!data) return [];

    const allWatching = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();

    // Filter only continue watching from current playlist
    const playlistWatching = allWatching.filter(item => item.playlistId === currentPlaylist);

    return playlistWatching.sort((a, b) => b.lastWatchedAt - a.lastWatchedAt);
  } catch (error) {
    console.error("Error reading series continue watching:", error);
    return [];
  }
};

// Build API URL with DNS support
const buildApiUrl = async (baseUrl, username, password, action, additionalParams = "") => {
  let apiUrl = `${baseUrl}/player_api.php?username=${username}&password=${password}&action=${action}`;
  if (additionalParams) {
    apiUrl += `&${additionalParams}`;
  }

  let finalApiUrl = apiUrl;
  try {
    const dnsResult = await dnsResolver.transformUrl(apiUrl);
    if (typeof dnsResult === "object" && dnsResult.url) {
      finalApiUrl = dnsResult.url;
    } else if (typeof dnsResult === "string" && dnsResult !== apiUrl) {
      finalApiUrl = dnsResult;
    }
  } catch (dnsError) {
    console.warn("DNS transformation failed:", dnsError);
  }

  return finalApiUrl;
};

export default function (filter) {
  return async pageIndex => {
    const username = playListUsername();
    const password = playListPassword();
    const baseUrl = playListUrl();

    if (!username || !password || !baseUrl) {
      console.error("Missing required parameters for Series Continue Watching");
      return [];
    }

    const cacheKey = `${baseUrl}_${username}_series_continue_watching`;

    // Check cache
    const cached = continueWatchingCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log("Using cached series continue watching data");
      return cached.data;
    }

    console.log("Loading Series Continue Watching...");

    try {
      const continueWatchingList = getContinueWatchingSeries();

      if (continueWatchingList.length === 0) {
        console.log("No series to continue watching");
        return [];
      }

      console.log(
        `Found ${continueWatchingList.length} series continue watching entries (before deduplication)`,
      );

      // DEDUPLICATE: Keep only the latest episode per series
      const latestPerSeries = new Map();

      continueWatchingList.forEach(item => {
        const seriesId = item.series_id;

        if (!latestPerSeries.has(seriesId)) {
          // First entry for this series
          latestPerSeries.set(seriesId, item);
        } else {
          // Compare timestamps and keep the more recent one
          const existing = latestPerSeries.get(seriesId);
          if (item.lastWatchedAt > existing.lastWatchedAt) {
            latestPerSeries.set(seriesId, item);
          }
        }
      });

      // Convert map back to array
      const deduplicatedList = Array.from(latestPerSeries.values());

      console.log(`After deduplication: ${deduplicatedList.length} unique series`);

      // Sort by most recent first
      deduplicatedList.sort((a, b) => b.lastWatchedAt - a.lastWatchedAt);

      // Extract unique series and category IDs
      const seriesIds = new Set();
      const categoryIds = new Set();

      deduplicatedList.forEach(item => {
        try {
          if (item.series_id) {
            seriesIds.add(item.series_id);
          }
          if (item.category_id) {
            categoryIds.add(item.category_id);
          }
        } catch (err) {
          console.warn("Error parsing series data:", err);
        }
      });

      console.log(`Fetching data for ${seriesIds.size} unique series from ${categoryIds.size} categories`);

      // Fetch series data from categories
      const allSeries = [];

      for (const categoryId of categoryIds) {
        try {
          const categoryUrl = await buildApiUrl(
            baseUrl,
            username,
            password,
            "get_series",
            `category_id=${categoryId}`,
          );

          const series = await fetchData(categoryUrl);

          if (Array.isArray(series)) {
            allSeries.push(...series);
          }
        } catch (error) {
          console.warn(`Failed to fetch series category ${categoryId}:`, error);
        }
      }

      // Match fetched series with deduplicated continue watching list
      const matchedSeries = [];

      for (const cwItem of deduplicatedList) {
        try {
          const seriesData = allSeries.find(s => String(s.series_id) === String(cwItem.series_id));

          if (seriesData) {
            matchedSeries.push({
              ...seriesData,
              continue_watching_time: cwItem.currentTime,
              continue_watching_duration: cwItem.duration,
              continue_watching_timestamp: cwItem.lastWatchedAt,
              continue_watching_episode_title: cwItem.episodeTitle || "",
              continue_watching_season: cwItem.season || "",
              continue_watching_episode: cwItem.episode || "",
            });
          } else {
            // Fallback: create basic series object
            matchedSeries.push({
              series_id: cwItem.series_id,
              name: cwItem.name || `Series ${cwItem.series_id}`,
              cover: cwItem.poster || "",
              category_id: cwItem.category_id || "unknown",
              rating_5based: cwItem.rating ? cwItem.rating / 2 : 0,
              continue_watching_time: cwItem.currentTime,
              continue_watching_duration: cwItem.duration,
              continue_watching_timestamp: cwItem.lastWatchedAt,
              continue_watching_episode_title: cwItem.episodeTitle || "",
              continue_watching_season: cwItem.season || "",
              continue_watching_episode: cwItem.episode || "",
            });
          }
        } catch (err) {
          console.warn("Error processing series item:", err);
        }
      }

      console.log(`Matched ${matchedSeries.length} series with full data (deduplicated)`);

      // Convert to tiles
      const limitedSeries = matchedSeries.slice(0, 20);
      const processedTiles = convertItemsToTilesSeries(limitedSeries);

      // Cache the result
      continueWatchingCache.set(cacheKey, {
        data: processedTiles,
        timestamp: Date.now(),
      });

      console.log(`Series continue watching processed: ${processedTiles.length} tiles (one per series)`);
      return processedTiles;
    } catch (error) {
      console.error("Error getting Series Continue Watching:", error);
      return [];
    }
  };
}

// ✅ MODIFIED: Add playlistId when saving
export const addSeriesToContinueWatching = (videoUrl, currentTime, seriesData = {}) => {
  try {
    const data = localStorage.getItem("series_continue_watching");
    let watchingList = data ? JSON.parse(data) : [];

    const currentPlaylist = getCurrentPlaylistId();

    const existingIndex = watchingList.findIndex(
      item =>
        item.series_id === seriesData.series_id &&
        item.season === seriesData.season &&
        item.episode === seriesData.episode &&
        item.playlistId === currentPlaylist,
    );

    const entry = {
      videoUrl,
      currentTime,
      duration: seriesData.duration || 0,
      lastWatchedAt: Date.now(),
      series_id: seriesData.series_id || "",
      name: seriesData.name || "",
      poster: seriesData.poster || "",
      category_id: seriesData.category_id || "",
      rating: seriesData.rating || 0,
      episodeTitle: seriesData.episodeTitle || "",
      season: seriesData.season || "",
      episode: seriesData.episode || "",
      playlistId: currentPlaylist, // ✅ ADDED THIS LINE
    };

    if (existingIndex !== -1) {
      watchingList[existingIndex] = entry;
    } else {
      watchingList.push(entry);
    }

    if (watchingList.length > 50) {
      watchingList = watchingList.slice(-50);
    }

    localStorage.setItem("series_continue_watching", JSON.stringify(watchingList));
    continueWatchingCache.clear();

    console.log("Added series to continue watching:", seriesData.name, seriesData.episodeTitle);
  } catch (error) {
    console.error("Error adding series to continue watching:", error);
  }
};

// ✅ MODIFIED: Check only in current playlist
export const getSerieContinueWatchingTime = (seriesId, season, episode) => {
  try {
    const data = localStorage.getItem("series_continue_watching");
    if (!data) return null;

    const watchingList = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();

    const entry = watchingList.find(
      item =>
        String(item.series_id) === String(seriesId) &&
        String(item.season) === String(season) &&
        String(item.episode) === String(episode) &&
        item.playlistId === currentPlaylist,
    );

    return entry ? entry.currentTime : null;
  } catch (error) {
    console.error("Error getting series continue watching time:", error);
    return null;
  }
};

// ✅ MODIFIED: Remove only from current playlist
export const removeSeriesFromContinueWatching = (seriesId, season, episode) => {
  try {
    const data = localStorage.getItem("series_continue_watching");
    if (!data) return;

    let watchingList = JSON.parse(data);
    const currentPlaylist = getCurrentPlaylistId();

    watchingList = watchingList.filter(
      item =>
        !(
          item.series_id === seriesId &&
          item.season === season &&
          item.episode === episode &&
          item.playlistId === currentPlaylist
        ),
    );

    localStorage.setItem("series_continue_watching", JSON.stringify(watchingList));
    continueWatchingCache.clear();

    console.log("Removed series from continue watching");
  } catch (error) {
    console.error("Error removing series:", error);
  }
};

export const clearSeriesContinueWatching = () => {
  localStorage.removeItem("series_continue_watching");
  continueWatchingCache.clear();
  console.log("Cleared all series continue watching");
};
